import { createContext } from 'preact'
import { useState, useContext, useMemo, useCallback } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { useOption } from 'libs/searchspring/hooks/useOption'
import { SortProvider } from 'libs/searchspring/hooks/useSort'
import { RANGE_INDICATOR } from '../components/collection/filters/FilterUtils'

const FiltersContext = createContext()

const FILTERS_BLACKLIST = []

function parseFiltersFromResults(results) {
  if (!results?.facets) return []
  return results.facets.filter((facet) => {
    return !FILTERS_BLACKLIST.some((field) => field === facet.field)
  })
}

export function FiltersProvider({ children }) {
  const { results, refine } = useSearchspringContext()
  const [isOpen, setIsOpen] = useState(false)

  const activeFilters = useOption('filters', {
    defaultValue: new Set(),
    syncWithURL: true
  })

  const fetchedFilters = useMemo(() => parseFiltersFromResults(results), [results])

  // Handles persisting initial filter values and just disabling them
  const [initialFilters, setInitialFilters] = useState(fetchedFilters)

  // Handles active filter state and 'cancel' function
  const [restorableFilters, setRestorableFilters] = useState(new Set())

  // Improved checkFilter that returns either the filter or the active filter that needs to be modified
  const checkFilter = useCallback(
    (filter) => {
      if (!filter.includes(RANGE_INDICATOR)) {
        return { filter, existingFilter: null }
      }

      const filterPrefix = filter.split(':')[0]

      for (const activeFilter of activeFilters) {
        if (activeFilter.includes(filterPrefix)) {
          return { filter, existingFilter: activeFilter }
        }
      }

      return { filter, existingFilter: null }
    },
    [activeFilters]
  )

  const handleToggleFilter = useCallback(
    (filter) => {
      const filters = new Set(activeFilters)
      const { filter: checkedFilter, existingFilter } = checkFilter(filter)

      if (existingFilter) {
        // If there's an existing filter with the same prefix, remove it first
        filters.delete(existingFilter)
      }

      if (filters.has(checkedFilter)) {
        filters.delete(checkedFilter)
      } else {
        filters.add(checkedFilter)
      }

      refine('filters', filters)
      refine('page', 1)
    },
    [activeFilters, refine, checkFilter]
  )

  const handleRemoveFilter = useCallback(
    (filter) => {
      const filters = new Set(activeFilters)
      const { existingFilter } = checkFilter(filter)

      const filterToRemove = existingFilter || filter

      if (filters.has(filterToRemove)) {
        filters.delete(filterToRemove)
        refine('filters', filters)
        refine('page', 1)
      }
    },
    [activeFilters, refine, checkFilter]
  )

  const handleClearFilters = useCallback(() => {
    refine('filters', new Set([]))
    refine('page', 1)
  }, [refine])

  const handleOpenFilters = () => {
    setIsOpen(true)
    setRestorableFilters(activeFilters)
    if (initialFilters.length === 0) setInitialFilters(fetchedFilters)
  }

  const handleCloseFilters = () => {
    setIsOpen(false)
  }

  const handleCancelFilters = () => {
    refine('filters', restorableFilters)
    setRestorableFilters(new Set())
    setIsOpen(false)
  }

  const value = {
    isOpen,
    activeFilters,
    fetchedFilters,
    initialFilters,
    restorableFilters,
    handleToggleFilter,
    handleRemoveFilter,
    handleClearFilters,
    handleOpenFilters,
    handleCloseFilters,
    handleCancelFilters
  }

  return (
    <FiltersContext.Provider value={value}>
      <SortProvider>{children}</SortProvider>
    </FiltersContext.Provider>
  )
}

export function useFilters() {
  const context = useContext(FiltersContext)
  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider')
  }
  return context
}
